import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, finalize, shareReplay, tap } from 'rxjs';
import { AREA_FILTER_MODE, ScopeLoadResult } from '../models/scope.model';
import { toAreaSelection } from '../utilities/scope-params.util';
import { ScopeService } from './scope.service';
import { ScopeStateService } from './scope-state.service';

@Injectable({ providedIn: 'root' })
export class ScopeControllerService {
  //* Services
  private readonly _state = inject(ScopeStateService);
  private readonly _service = inject(ScopeService);
  private readonly _ngZone = inject(NgZone);

  //* Variables
  private _request$: Observable<ScopeLoadResult> | null = null;

  //* Public Methods
  public loadScope(): Observable<ScopeLoadResult> {
    if (this._request$) return this._request$;

    this._state.setLoading(true);
    this._request$ = this._service.getScope().pipe(
      tap((result) => this._ngZone.run(() => this._applyResult(result))),
      finalize(() => this._ngZone.run(() => this._state.setLoading(false))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this._request$;
  }

  public refreshScope(): Observable<ScopeLoadResult> {
    this._request$ = null;
    return this.loadScope();
  }

  public selectMenuValue(value: string): void {
    const selection = toAreaSelection(value, this._state.scope());

    switch (selection.mode) {
      case AREA_FILTER_MODE.ALL:
        this._state.selectAll();
        return;
      case AREA_FILTER_MODE.CUSTOM:
        this._state.selectAreas(selection.areaIds);
        return;
      default:
        this._state.selectDefault();
    }
  }

  public resetSelection(): void {
    this._state.selectDefault();
  }

  public clear(): void {
    this._request$ = null;
    this._state.reset();
  }

  //* Private Methods
  private _applyResult(result: ScopeLoadResult): void {
    if (result.scope) {
      this._state.setScope(result.scope);
      return;
    }

    this._state.setUnavailable();
  }
}
